import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Plane, Loader2, ArrowRight, Briefcase } from "lucide-react";

export default function FlightResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { formatPrice } = useApp();
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);

  const search = {
    origin: params.get("origin"), destination: params.get("destination"),
    departDate: params.get("departDate") || new Date(Date.now() + 21 * 864e5).toISOString().slice(0, 10),
    returnDate: params.get("returnDate") || null,
    adults: +(params.get("adults") || 1),
  };

  useEffect(() => {
    setLoading(true);
    api.post("/flights/search", search).then((r) => setFlights(r.data.flights)).catch(() => setFlights([])).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [params]);

  const book = (f) => navigate("/checkout", { state: { item: {
    productType: "flight",
    title: `${f.airline} · ${f.originCity} → ${f.destinationCity}`,
    subtitle: `${f.flightNumber} · ${f.departDate} · ${f.departTime}–${f.arriveTime}`,
    image: null,
    priceEur: f.priceEur,
    meta: { ...f },
  } } });

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="pt-24 max-w-5xl mx-auto px-5 lg:px-8">
        <div className="bg-[#0B2038]/5 border border-[#E2E8F0] rounded-xl px-4 py-2 text-xs text-[#475569] mb-4 inline-block">
          Demo flight inventory — live flight booking coming soon.
        </div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-[#0B2038] mb-6">
          {loading ? "Searching flights…" : `${flights.length} flights · ${search.origin} → ${search.destination}`}
        </h1>
        {loading ? (
          <div className="flex flex-col items-center py-24 text-[#475569]"><Loader2 className="w-8 h-8 animate-spin mb-3 text-[#009E9E]" />Finding the best fares…</div>
        ) : (
          <div className="space-y-4">
            {flights.map((f) => (
              <div key={f.id} data-testid={`flight-card-${f.id}`} className="bg-white border border-[#E2E8F0] rounded-2xl p-5 flex flex-wrap items-center gap-6 transition-transform duration-300 hover:-translate-y-1">
                <div className="flex items-center gap-3 min-w-[170px]">
                  <span className="w-11 h-11 rounded-full bg-[#0B2038] text-white flex items-center justify-center font-bold text-sm">{f.airlineCode}</span>
                  <div>
                    <p className="font-semibold text-[#0B2038] text-sm">{f.airline}</p>
                    <p className="text-xs text-[#475569]">{f.flightNumber}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-1">
                  <div className="text-center"><p className="font-display text-xl font-bold text-[#0B2038]">{f.departTime}</p><p className="text-xs text-[#475569]">{f.origin}</p></div>
                  <div className="flex-1 flex flex-col items-center min-w-[90px]">
                    <p className="text-xs text-[#475569]">{Math.floor(f.durationMin / 60)}h {f.durationMin % 60}m</p>
                    <div className="w-full flex items-center gap-1"><span className="h-px bg-[#E2E8F0] flex-1" /><Plane className="w-3.5 h-3.5 text-[#009E9E]" /><span className="h-px bg-[#E2E8F0] flex-1" /></div>
                    <p className="text-xs text-[#475569]">{f.stops === 0 ? "Direct" : `${f.stops} stop`}</p>
                  </div>
                  <div className="text-center"><p className="font-display text-xl font-bold text-[#0B2038]">{f.arriveTime}</p><p className="text-xs text-[#475569]">{f.destination}</p></div>
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl font-bold text-[#0B2038]">{formatPrice(f.priceEur)}</p>
                  <p className="text-xs text-[#475569] flex items-center gap-1 justify-end"><Briefcase className="w-3 h-3" />Incl. baggage</p>
                  <Button data-testid={`flight-select-${f.id}`} onClick={() => book(f)} className="mt-2 rounded-full bg-[#009E9E] hover:bg-[#007A7A] text-white font-semibold h-9 px-5 text-sm">Select <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
